import { useState } from 'react';
import { useNavigate } from '@/lib/router-dom';
import submittalsService from '@/services/api/submittalsService.js';

const buildSubmittalPayload = (selectedDataElement, status, payload) => ({
    data_element_id: selectedDataElement.id,
    status,
    payload,
    submission_month: payload?.project_details?.submission_month || null,
    consultant_email: payload?.approver?.approver_email || '',
    consultant_name: payload?.approver?.approver_name || '',
});

const useSubmittalActions = ({
    submittalId,
    isEditMode,
    selectedDataElement,
    payload,
    addToastMessage,
    validateSchema,
    validateErrors,
    setIsDirty,
    setIsSubmitClicked,
    floatingPanelRef,
}) => {
    const { goTo } = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitWithStatus = async (status) => {
        const data = buildSubmittalPayload(selectedDataElement, status, payload);
        if (isEditMode) {
            return submittalsService.updateSubmittal(submittalId, data);
        }
        return submittalsService.createSubmittal(data);
    };

    const handleSaveDraft = async () => {
        setIsSaving(true);
        try {
            const [err] = await submitWithStatus('DRAFT');
            if (err) {
                addToastMessage(err, 'error');
            } else {
                addToastMessage('E-Form saved as draft.', 'success');
                window.dataLayer?.push({
                    event: 'submittals_step2_save_draft',
                    data_element_name: selectedDataElement?.name || selectedDataElement?.id,
                    submission_month: payload?.project_details?.submission_month || '',
                    project_contract: payload?.project_details?.project_contract_name || '',
                });
                goTo('/submittals');
            }
        } catch {
            addToastMessage('Failed to save draft.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitClicked(true);
        const isValid = validateSchema();

        if (!isValid) {
            validateErrors();
            setIsDirty(true);
            floatingPanelRef.current?.open();
            return;
        }

        setIsSubmitting(true);
        try {
            const [err] = await submitWithStatus('PENDING_REVIEW');
            if (err) {
                addToastMessage(err, 'error');
            } else {
                addToastMessage(`E-Form submitted to ${payload?.approver?.approver_email} for review.`, 'success');
                window.dataLayer?.push({
                    event: 'submittals_step2_submitted',
                    data_element_name: selectedDataElement?.name || selectedDataElement?.id,
                    submission_month: payload?.project_details?.submission_month || '',
                    project_contract: payload?.project_details?.project_contract_name || '',
                });
                goTo('/submittals');
            }
        } catch {
            addToastMessage('Failed to submit.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateApprover = async () => {
        setIsSaving(true);
        try {
            const [err] = await submittalsService.updateApprover(submittalId, {
                consultant_email: payload?.approver?.approver_email || '',
                consultant_name: payload?.approver?.approver_name || '',
            });
            if (err) {
                addToastMessage(err, 'error');
            } else {
                addToastMessage('Approver updated.', 'success');
                window.dataLayer?.push({
                    event: 'submittals_edit_save_and_submit',
                    data_element_name: selectedDataElement?.name || selectedDataElement?.id,
                    submission_month: payload?.project_details?.submission_month || '',
                    project_contract: payload?.project_details?.project_contract_name || '',
                });
                goTo('/submittals');
            }
        } catch {
            addToastMessage('Failed to update approver.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isSaving,
        isSubmitting,
        handleSaveDraft,
        handleSubmit,
        handleUpdateApprover,
    };
};

export default useSubmittalActions;
